import { Checkbox } from '@src/components/ui/checkbox';
import { Input } from '@src/components/ui/input';
import { RadioGroup } from '@src/components/ui/radio-group';
import { cn } from '@src/lib/utils';

import useLangText from '@shared/hooks/useLangText';
import type {
    ProcessFieldBase,
    ProcessFieldSelectService,
} from '@shared/types/process';
import type { DefaultOptionType } from '@shared/types/types';

import type { ProcessFieldSelectField } from './ProcessFieldSelectField';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { ShadcnForm } from '@shadcn-features/core';
import { Search } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = Omit<
    React.ComponentProps<typeof ProcessFieldSelectField>,
    'showSearch' | 'field'
> & {
    field:
        | React.ComponentProps<typeof ProcessFieldSelectField>['field']
        | (ProcessFieldBase & ProcessFieldSelectService);
    showSearch?: boolean;
    options?: DefaultOptionType[];
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    filterOptionsLocally?: boolean;
    onListScroll?: React.UIEventHandler<HTMLDivElement>;
    listFooter?: React.ReactNode;
    emptyState?: React.ReactNode;
};

export function ProcessFieldSelectFieldAiChat({
    field,
    name,
    label,
    initialValue,
    defaultValue,
    required,
    rules,
    disabled,
    readOnly,
    hidden,
    className,
    fieldProps,
    showSearch,
    options: suppliedOptions,
    searchValue,
    onSearchChange,
    filterOptionsLocally = true,
    onListScroll,
    listFooter,
    emptyState,
}: Props) {
    const getLangText = useLangText();
    const { t } = useTranslation();
    const form = ShadcnForm.useFormInstance();
    const id = useId();
    const [search, setSearch] = useState('');
    const multiple = field.options.mode === 'multiple';
    const searchable = showSearch ?? multiple;
    const locked = disabled || readOnly;
    const maxCount = Number(field.options.maxCount);
    const options = useMemo(
        () =>
            (suppliedOptions ?? field.options.options ?? []).map(
                (option, index) => ({
                    value: option.value,
                    label: getLangText(option.label),
                    disabled: option.disabled,
                    index,
                }),
            ),
        [suppliedOptions, field.options.options, getLangText],
    );
    const currentSearch = searchValue ?? search;
    const needle =
        searchable && filterOptionsLocally
            ? currentSearch.trim().toLocaleLowerCase()
            : '';
    const visibleOptions = options.filter((option) =>
        option.label.toLocaleLowerCase().includes(needle),
    );

    return (
        <ShadcnForm.Item
            name={name}
            defaultValue={defaultValue ?? initialValue}
            required={required}
            rules={rules}
            hidden={hidden}
            readOnly={readOnly}
            className={cn('mb-0! w-full', className)}
        >
            {({ value, onChange, ref }) => {
                const selected = Array.isArray(value)
                    ? field.typeId === 'SelectIdp'
                        ? value.map(String)
                        : value
                    : [];
                const change = (
                    next: string | number | (string | number)[],
                    option: (typeof options)[number],
                ) => {
                    if (locked) return;
                    onChange(next);
                    fieldProps?.onChange?.(next, option, form);
                };
                const rowClassName =
                    'flex min-h-10 w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left text-sm leading-5 text-(--color-card-text-secondary) hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:bg-white/5';
                const rows = visibleOptions.map((option) => {
                    const checked = multiple
                        ? selected.includes(option.value)
                        : value === option.value;
                    const rowDisabled = Boolean(
                        locked ||
                        option.disabled ||
                        (multiple &&
                            !checked &&
                            maxCount > 0 &&
                            selected.length >= maxCount),
                    );
                    const optionId = `${id}-${option.index}`;

                    if (multiple) {
                        return (
                            <label
                                key={JSON.stringify(option.value)}
                                htmlFor={optionId}
                                className={cn(
                                    rowClassName,
                                    'cursor-pointer border-b border-solid border-(--color-card-text)/5',
                                    checked &&
                                        'bg-black/5 text-(--color-card-text) dark:bg-white/5',
                                    rowDisabled &&
                                        'cursor-not-allowed opacity-50',
                                )}
                            >
                                <Checkbox
                                    id={optionId}
                                    ref={
                                        option.index ===
                                        visibleOptions[0]?.index
                                            ? ref
                                            : undefined
                                    }
                                    checked={checked}
                                    disabled={rowDisabled}
                                    className='border-border border-solid bg-(--color-card) shadow-xs'
                                    onCheckedChange={(next) =>
                                        change(
                                            next
                                                ? [...selected, option.value]
                                                : selected.filter(
                                                      (item) =>
                                                          item !== option.value,
                                                  ),
                                            option,
                                        )
                                    }
                                />
                                <span className='min-w-0 break-words'>
                                    {option.label}
                                </span>
                            </label>
                        );
                    }

                    return (
                        <RadioGroupPrimitive.Item
                            key={JSON.stringify(option.value)}
                            value={JSON.stringify(option.value)}
                            ref={
                                option.index === visibleOptions[0]?.index
                                    ? ref
                                    : undefined
                            }
                            disabled={rowDisabled}
                            className={cn(
                                rowClassName,
                                'min-h-11 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-black/5 data-[state=checked]:font-medium data-[state=checked]:text-(--color-card-text) dark:data-[state=checked]:bg-white/5',
                            )}
                        >
                            <span
                                aria-hidden
                                className={cn(
                                    'flex size-[27px] shrink-0 items-center justify-center rounded-md bg-(--color-bg-grey-v2) text-(--color-card-text-secondary)',
                                    checked &&
                                        'bg-(--color-bg-grey-v1) text-(--color-card-text)',
                                )}
                            >
                                {option.index + 1}
                            </span>
                            <span className='min-w-0 break-words'>
                                {option.label}
                            </span>
                        </RadioGroupPrimitive.Item>
                    );
                });

                return (
                    <div className='w-full text-(--color-card-text)'>
                        <div
                            id={`${id}-label`}
                            title={
                                getLangText(field.tooltip ?? '') || undefined
                            }
                            className='border-b border-solid border-(--color-card-text)/10 px-2.5 py-4 text-sm leading-5 font-medium'
                        >
                            {label}
                            {required && (
                                <span className='ml-1 text-(--color-danger)'>
                                    *
                                </span>
                            )}
                        </div>
                        <div className='space-y-3 py-3'>
                            {searchable && (
                                <div className='relative rounded-xl [box-shadow:0_0_6px_1px_rgba(0,0,0,0.05)]'>
                                    <Search
                                        aria-hidden
                                        className='pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-(--color-card-subtitle)'
                                    />
                                    <Input
                                        type='search'
                                        aria-label={t('translation:search')}
                                        placeholder={t('translation:search')}
                                        value={currentSearch}
                                        disabled={disabled}
                                        onChange={(event) => {
                                            setSearch(event.target.value);
                                            onSearchChange?.(
                                                event.target.value,
                                            );
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter')
                                                event.preventDefault();
                                        }}
                                        className='h-11 rounded-xl border border-solid border-(--color-card-text)/10 bg-(--color-card) pr-4 pl-10 text-sm focus-visible:outline-2! focus-visible:outline-(--color-card-text)! focus-visible:outline-solid!'
                                    />
                                </div>
                            )}
                            {multiple ? (
                                <div
                                    role='group'
                                    aria-labelledby={`${id}-label`}
                                    className='max-h-60 overflow-y-auto overscroll-contain'
                                    onScroll={onListScroll}
                                >
                                    {rows}
                                    {listFooter}
                                </div>
                            ) : (
                                <RadioGroup
                                    aria-labelledby={`${id}-label`}
                                    value={
                                        value == null
                                            ? ''
                                            : JSON.stringify(value)
                                    }
                                    onValueChange={(next) => {
                                        const option = options.find(
                                            (item) =>
                                                JSON.stringify(item.value) ===
                                                next,
                                        );
                                        if (option)
                                            change(option.value, option);
                                    }}
                                    className='max-h-60 gap-0 overflow-y-auto overscroll-contain'
                                    onScroll={onListScroll}
                                >
                                    {rows}
                                    {listFooter}
                                </RadioGroup>
                            )}
                            {visibleOptions.length === 0 &&
                                (emptyState ?? (
                                    <p
                                        role='status'
                                        className='px-2.5 py-3 text-sm text-(--color-card-text-secondary)'
                                    >
                                        {t('translation:noData')}
                                    </p>
                                ))}
                        </div>
                    </div>
                );
            }}
        </ShadcnForm.Item>
    );
}
